export const PAYMENT_FLOWS = [
  'card_registration',
  'card_change',
  'initial_subscription',
  'plan_change',
  'recurring',
  'onetime_product',
  'onetime_custom',
  'business_payment',
] as const;
export type PaymentFlow = (typeof PAYMENT_FLOWS)[number];
export type PaymentFailureMetadata = {
  payment_flow: PaymentFlow;
  pg_provider: 'toss' | 'payple' | 'unknown';
  pg_error_code?: string;
  /** PG 가 준 원문 메시지. 서버를 거치지 않고 클라가 직접 받은 경우에만 채운다(서버는 payment_failed 에 남긴다). */
  pg_error_message?: string;
  zep_status?: number | string;
  payment_reference_id?: string;
  user_id?: number;
  space_id?: number;
};

const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : {};
export function readPaymentFailure(
  value: unknown,
): Partial<PaymentFailureMetadata> {
  const source = record(value);
  const result: Partial<PaymentFailureMetadata> = {};
  if (PAYMENT_FLOWS.some(flow => flow === source.payment_flow))
    result.payment_flow = source.payment_flow as PaymentFlow;
  if (
    source.pg_provider === 'toss' ||
    source.pg_provider === 'payple' ||
    source.pg_provider === 'unknown'
  )
    result.pg_provider = source.pg_provider;
  for (const key of ['user_id', 'space_id'] as const) {
    const id = source[key];
    if (typeof id === 'number' && Number.isSafeInteger(id) && id > 0)
      result[key] = id;
  }
  if (
    typeof source.zep_status === 'number' &&
    Number.isFinite(source.zep_status)
  )
    result.zep_status = source.zep_status;
  else if (
    typeof source.zep_status === 'string' &&
    /^[-A-Z0-9_]{1,100}$/i.test(source.zep_status)
  )
    result.zep_status = source.zep_status;
  if (
    typeof source.pg_error_code === 'string' &&
    /^[-A-Z0-9_]{1,100}$/i.test(source.pg_error_code)
  )
    result.pg_error_code = source.pg_error_code;
  if (typeof source.pg_error_message === 'string') {
    const message = sanitizePgMessage(source.pg_error_message);
    if (message) result.pg_error_message = message;
  }
  if (
    typeof source.payment_reference_id === 'string' &&
    /^(toss_order|payple_order):[A-Za-z0-9_-]{1,200}$/.test(
      source.payment_reference_id,
    )
  )
    result.payment_reference_id = source.payment_reference_id;
  return result;
}
export function paymentFailureFromError(
  error: unknown,
): Partial<PaymentFailureMetadata> {
  const e = record(error);
  const response = record(record(e.response).data);
  return readPaymentFailure(
    e.paymentFailure ??
      record(e.bindingData).paymentFailure ??
      record(e.data).paymentFailure ??
      record(response.data).paymentFailure ??
      response.paymentFailure,
  );
}
export function paymentFailureFromQuery(
  value: unknown,
): Partial<PaymentFailureMetadata> {
  if (typeof value !== 'string' || value.length > 2000) return {};
  try {
    // URL 쿼리는 사용자가 고칠 수 있다. 사람 축 ID 는 받지 않는다(서버 payment_failed 에 남는다).
    const { user_id: _userId, ...failure } = readPaymentFailure(
      JSON.parse(value),
    );
    return failure;
  } catch {
    return {};
  }
}
// 서버 PaymentFailureEvent.SanitizeMessage 와 같은 기준(이메일·카드번호, 500자). URL 에서 온 값이라 URL 도 지운다.
// 마스킹 정규식 전에 1000자로 자른다. 잘린 조각은 마지막 500자 절단으로 결과에 남지 않는다.
function sanitizePgMessage(message: string): string {
  return (
    message
      .trim()
      .replace(/\s+/g, ' ')
      .slice(0, 1000)
      .replace(/https?:\/\/\S+/gi, '[REDACTED_URL]')
      .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[REDACTED_EMAIL]')
      // 13~19자리 연속·4-4-4-x 묶음과 Amex 4-6-5 묶음.
      .replace(
        /\b(?:\d{4}[- ]?\d{4}[- ]?\d{4}[- ]?\d{1,7}|\d{4}[- ]?\d{6}[- ]?\d{5})\b/g,
        '[REDACTED_CARD]',
      )
      .slice(0, 500)
  );
}
